import { useEffect, useState } from 'react';
import { calculateAge, msUntilNextBirthday, type IsoDate } from '../utils/age';

/**
 * setTimeout overflows beyond ~24.8 days, so long waits are split into chunks.
 * The chunk also re-syncs the age after the device has been asleep.
 */
const MAX_TIMEOUT_MS = 6 * 60 * 60 * 1000;

/**
 * Current age for a birthday, kept up to date while the page stays open:
 * the displayed number flips the moment the birthday begins.
 */
export function useAge(birthDate: IsoDate): number {
  const [age, setAge] = useState(() => calculateAge(birthDate));

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      setAge(calculateAge(birthDate));
      // +1s so the timer never fires a hair before midnight and misses the flip.
      const wait = Math.min(msUntilNextBirthday(birthDate) + 1000, MAX_TIMEOUT_MS);
      timer = setTimeout(tick, wait);
    };
    tick();

    // Background tabs get their timers throttled — recheck when we're visible again.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timer);
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [birthDate]);

  return age;
}

export default useAge;
